import { useCallback, useMemo, createContext, useContext, useEffect } from 'react';
import { useRouter } from 'next/router';
import each from 'lodash/each';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import findLastIndex from 'lodash/findLastIndex';
import filter from 'lodash/filter';
import type { NextPageContext } from 'next/types';
import includes from 'lodash/includes';
import last from 'lodash/last';

import { SignupChallengeStep } from '@jerry-serverless/enums/user_challenge';
import type { FlowType } from '@jerry-serverless/types';

import type { AppData } from '../types/app_data';
import { AppStepSection } from '../types/app_data';
import { makeQueryStr, BASE_PATH } from '../utils/url';
import { trackEvent, trackOnboarded, trackSignedUp } from '../utils/analytics';
import { ssrGetFeatureValue } from '../utils/analytics/growthbook';
import {
  ABTestGasBuddy,
  ABTestWebappNewUserOnboarding,
  ABTestWebappNewUserOnboardingLte50,
  useGetWebappNewUserOnboardingVariant,
} from '../utils/enabled_features/ab_tests';
import { WEB_APP_PATH, WEB_APP_QUOTES_PATH, isFromGasbuddy } from '../utils/webapp';
import { isGarageFlow } from '../utils/garage';

import type { Step } from './types/step';
import { Direction } from './types/step';
import type { StepManagerContextType, StepManagerEffectReturnType } from './types/step_manager';
import { getCreateAccountSteps } from './create_account';
import { getChallengeSteps } from './challenges';
import { getOnboardingSteps } from './onboarding';
import { getFinishedSteps } from './finished';
import { QuotesStep } from './quotes/quotes.step';

/**
 * Generate STEPS based on the app data.
 * @param appData
 * @returns
 */
export const getStepsConfig = (flowType?: FlowType): Record<AppStepSection, Step[]> => {
  return {
    [AppStepSection.CreateAccount]: getCreateAccountSteps(flowType),
    [AppStepSection.Challenge]: getChallengeSteps(flowType),
    [AppStepSection.Onboarding]: getOnboardingSteps(flowType),
    [AppStepSection.Finished]: getFinishedSteps(flowType),
    [AppStepSection.Quotes]: [QuotesStep],
  };
};

/**
 * Returns true, if the pathname is in quotes section.
 *
 * @param pathname
 * @returns
 */
export const isInQuotePages = (pathname: string): boolean => {
  return includes(['/quotes', '/quotedetails'], pathname);
};

/**
 * Returns true, if the pathname is in need quote pages.
 *
 * @param pathname
 * @returns
 */
export const isInNeedQuotePages = (pathname: string): boolean => {
  return includes(['/finish', '/quotes', '/quotedetails'], pathname);
};

/**
 * Determine the initial route to navigate. This is performed on the server side.
 *
 * @param pageCtx
 * @param appData
 */
export const redirectOnLoad = async (
  pageCtx: NextPageContext,
  appData: AppData,
  pathname?: string
): Promise<string | null> => {
  pathname = pathname || pageCtx.pathname;
  const queryStr = makeQueryStr(pageCtx.query);
  const STEPS = getStepsConfig(appData.flowType);

  if ((pageCtx.query['inviteCode'] && pageCtx.query['inviter']) || appData.inviteV3DataInCookies) {
    appData.isOnboardingLite = true;
    appData.stepSection = AppStepSection.CreateAccount;
    appData.selectedService = { insurance: false, garage: false, dwep: true, refi: false };
    if (pageCtx.query['inviteCode'] && pageCtx.query['inviter']) {
      appData.inviteV3DataInParams = {
        inviterCode: pageCtx.query['inviteCode'].toString(),
        inviterName: pageCtx.query['inviter'].toString(),
      };
    }
  }

  // Rapid onboard for prefill quoting 9 test: https://app.asana.com/0/1202292931304613/1209918454215747/f
  if (
    appData.stepSection === AppStepSection.Finished &&
    appData.signupChallengeStep !== SignupChallengeStep.Finished &&
    !!appData.featureData?.prefillQuotingVariation
  ) {
    return WEB_APP_QUOTES_PATH;
  }

  // If user is from gasbuddy, don't enter the existing redirect ABTests, enter the gasbuddy ABTest.
  const userFromGasBuddy = isFromGasbuddy(appData.partner?.partner);
  if (appData.stepSection === AppStepSection.Finished && userFromGasBuddy) {
    if ((await ssrGetFeatureValue(appData, ABTestGasBuddy, 0)) === 1) {
      return WEB_APP_PATH;
    }
  }

  // TODO: This is for test webapp_new_user_onboarding
  if (
    appData.stepSection === AppStepSection.Finished &&
    appData.signupChallengeStep !== SignupChallengeStep.Finished &&
    appData.dateOfBirthMasked &&
    !userFromGasBuddy
  ) {
    const birthYear = parseInt(appData.dateOfBirthMasked.slice(0, 4), 10);
    if (!Number.isNaN(birthYear)) {
      const age = new Date().getFullYear() - birthYear;
      if (age > 50) {
        if ((await ssrGetFeatureValue(appData, ABTestWebappNewUserOnboarding, 0)) === 1) {
          return WEB_APP_QUOTES_PATH;
        }
      } else {
        if ((await ssrGetFeatureValue(appData, ABTestWebappNewUserOnboardingLte50, 0)) === 1) {
          return WEB_APP_QUOTES_PATH;
        }
      }
    }
  }

  // Finds the first step that can be shown in the given section.
  let foundStep = find(STEPS[appData.stepSection], (step) => step.canShow(appData));
  if (!foundStep) {
    return null;
  }
  const finishedLastStep = last(STEPS[AppStepSection.Finished]);
  if (foundStep === finishedLastStep && isInQuotePages(pathname) && QuotesStep.canShow(appData)) {
    foundStep = QuotesStep;
  }

  return foundStep.pathname !== pathname ? `${BASE_PATH}${foundStep.pathname}${queryStr}` : null;
};

/**
 * Step manager Context keeps track of the step-to-step navigation.
 */
export const StepManagerContext = createContext<StepManagerContextType>({
  // This is garbage initial data. It gets overriden by context provider.
  data: {
    appData: { stepSection: AppStepSection.CreateAccount },
    stepStack: [],
    direction: Direction.Right,
    changingStepSection: false,
  },
  setData: () => undefined,
});

interface CanShowStepsResult {
  showSteps: Step[];
  stepIdToIndexMap: Map<string, number>;
}

/**
 * Effect that is used inside step pages.
 * It provides all the data needed to render current page as well as
 * functions to navigate backward and forward.
 *
 * @param stepID
 */
export const useStepManager = (stepID: string): StepManagerEffectReturnType => {
  const { data, setData } = useContext(StepManagerContext);
  const router = useRouter();
  const STEPS = useMemo(() => getStepsConfig(data.appData.flowType), [data.appData.flowType]);

  // Determine ordered sequence of steps that can be shown:
  const getCanShowSteps = useMemo(() => {
    // appData -> sectionSteps -> cachedResult
    const cache = new WeakMap<AppData, WeakMap<Step[], CanShowStepsResult>>();
    return (
      appData: AppData = data.appData,
      sectionSteps: Step[] = STEPS[data.appData.stepSection]
    ): CanShowStepsResult => {
      // check if we have a cached result for this appData and sectionSteps.
      const cachedResult = cache.get(appData)?.get(sectionSteps);
      if (cachedResult) return cachedResult;

      const showSteps: Step[] = [];
      // Store the mappings of step id and it's index in showSteps.
      const stepIdToIndexMap: Map<string, number> = new Map();

      each(sectionSteps, (step) => {
        // Push the step to showSteps to fix page change step's can show issue.
        if (
          step.canShow(appData) ||
          (!step.canShowSkipIdCheck && sectionSteps === STEPS[data.appData.stepSection] && step.id === stepID)
        ) {
          showSteps.push(step);
          stepIdToIndexMap.set(step.id, showSteps.length - 1);
        }
      });

      const result: CanShowStepsResult = {
        showSteps,
        stepIdToIndexMap,
      };
      if (!cache.has(appData)) {
        cache.set(appData, new WeakMap());
      }
      cache.get(appData)?.set(sectionSteps, result);

      return result;
    };
  }, [data.appData, stepID, STEPS]);

  /**
   * Get whole steps that can be shown in create account section and onboarding section, and return the list of all categories.
   */
  const getWholeCanShowStepsOfCreateAccountAndOnboarding = useCallback(
    (newAppData?: AppData) => {
      let wholeCanShowSteps: Step[] = [];
      if (data.appData.stepSection === AppStepSection.CreateAccount) {
        wholeCanShowSteps = wholeCanShowSteps.concat(getCanShowSteps().showSteps);
        wholeCanShowSteps = wholeCanShowSteps.concat(
          getCanShowSteps(newAppData, STEPS[AppStepSection.Onboarding]).showSteps
        );
      } else {
        wholeCanShowSteps = wholeCanShowSteps.concat(
          getCanShowSteps(newAppData, STEPS[AppStepSection.CreateAccount]).showSteps
        );
        wholeCanShowSteps = wholeCanShowSteps.concat(getCanShowSteps().showSteps);
      }
      const wholeCanShowStepsByCategorySet: Set<string> = new Set();
      each(wholeCanShowSteps, (step) => {
        if (step.category) {
          const category = step.category(newAppData || data.appData);
          // If it's currently in CreateAccount section, there won't be duplicate steps because the if clause
          // above only keep steps in the CreateAccount section. But if it's in the Onboarding section, there will be
          // steps of the same category that are in both sections, and we want to keep only the latest one.
          // Delete the category first then add it, so that if it already exists, it will be moved to the end.
          wholeCanShowStepsByCategorySet.delete(category);
          wholeCanShowStepsByCategorySet.add(category);
        }
      });

      return {
        wholeCanShowSteps,
        wholeCanShowStepsByCategory: [...wholeCanShowStepsByCategorySet],
      };
    },
    [data.appData, getCanShowSteps, STEPS]
  );

  // Index of the current step:
  const getCurrentStepIdx = useCallback(
    (newAppData?: AppData) => {
      return findIndex(getCanShowSteps(newAppData).showSteps, (step) => step.id === stepID);
    },
    [stepID, getCanShowSteps]
  );

  // Current step:
  const getCurrentStep = useCallback(
    (newAppData?: AppData) => {
      return getCanShowSteps(newAppData).showSteps[getCurrentStepIdx(newAppData)];
    },
    [getCurrentStepIdx, getCanShowSteps]
  );

  /**
   * Get indicator data of current step.
   */
  const getStepIndicatorData = useCallback(() => {
    const { wholeCanShowSteps, wholeCanShowStepsByCategory } = getWholeCanShowStepsOfCreateAccountAndOnboarding();
    const currentStep = getCurrentStep();

    const categoryOfCurrentStep = currentStep?.category?.(data.appData);
    const stepIndexInCategory = findIndex(
      wholeCanShowStepsByCategory,
      (category) => category === categoryOfCurrentStep
    );
    const stepIndexInWholeSteps = findIndex(wholeCanShowSteps, (currentStep) => currentStep.id === stepID);

    return {
      stepIndexInCategory,
      stepCategoryCount: wholeCanShowStepsByCategory.length,
      categories: wholeCanShowStepsByCategory,
      stepIndexInWholeSteps,
      stepCountInWholeSteps: wholeCanShowSteps.length,
      categoryOfCurrentStep,
    };
  }, [data.appData, getCurrentStep, getWholeCanShowStepsOfCreateAccountAndOnboarding, stepID]);

  // Next step (after `currentStepIdx`):
  const getNextStep = useCallback(
    (newAppData?: AppData) => {
      const { showSteps: canShowSteps, stepIdToIndexMap } = getCanShowSteps(newAppData || data.appData);
      // If current step has specialNextStep setting, check the condition.
      const currentStep = getCurrentStep(newAppData || data.appData);
      const specialNextStepId =
        currentStep?.specialNextStep?.enable(newAppData || data.appData) && currentStep.specialNextStep?.nextStepId;
      const specialNextStepIndex = specialNextStepId && stepIdToIndexMap.get(specialNextStepId);
      if (typeof specialNextStepIndex === 'number') {
        return canShowSteps[specialNextStepIndex];
      }
      return canShowSteps[getCurrentStepIdx(newAppData || data.appData) + 1];
    },
    [data.appData, getCanShowSteps, getCurrentStepIdx, getCurrentStep]
  );

  // Fire trackevent when user lands on new page.
  useEffect(() => {
    void trackEvent('SignupEnteredStep', {
      eventMetadata: {
        path: router.pathname,
        partner: data.appData.partner?.partner || '',
        flowType: data.appData.flowType,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefetch next step.
  useEffect(() => {
    const nextStep = getNextStep();
    if (nextStep) {
      void router.prefetch(nextStep.pathname);
    }
  }, [getNextStep, router]);

  // Can go to next step?
  const canGoNext = useCallback(
    (newAppData?: AppData) => {
      // Current step should have collected its own data.
      const currentStep = getCurrentStep(newAppData || data.appData);
      if (!currentStep?.hasOwnData(newAppData || data.appData, router)) {
        return false;
      }

      // Next step should have all the prerequisite data.
      const nextStep = getNextStep(newAppData || data.appData);
      return !!(nextStep && nextStep.hasData(newAppData || data.appData));
    },
    [getCurrentStep, data.appData, router, getNextStep]
  );

  // Can go back?
  const canGoBack = useCallback(
    (numPages = 1) => {
      const idx = getCurrentStepIdx();

      // Find the step that allows back to self.
      const prevSteps = filter(
        getCanShowSteps().showSteps.slice(0, idx),
        ({ allowBackToSelf = true }) => allowBackToSelf
      );
      return prevSteps.length >= numPages;
    },
    [getCurrentStepIdx, getCanShowSteps]
  );

  /**
   * Function that step pages will use to update app data.
   */
  const setAppData = useCallback(
    (newAppData: AppData | ((prevAppData: AppData) => AppData)) => {
      setData((prev) => ({
        ...prev,
        appData: typeof newAppData === 'function' ? newAppData(prev.appData) : newAppData,
      }));
    },
    [setData]
  );

  const getWebappNewUserOnboardingVariant = useGetWebappNewUserOnboardingVariant(data.appData);

  /**
   * Navigate to next step in the funnel.
   *
   * @param newAppData - Optionally update the app data before navigation.
   */
  const goNext = useCallback(
    async (newAppData?: AppData, action: 'push' | 'replace' = 'push') => {
      const trackCompletedStepEvent = () => {
        void trackEvent('SignupCompletedStep', {
          eventMetadata: {
            path: router.pathname,
            partner: data.appData.partner?.partner || '',
            // TODO: remove these
            selectedService: data.appData.selectedService,
            stepSection: data.appData.stepSection,
            state: data.appData.address?.state,
            cars: data.appData.cars?.length,
            isAutoInsured: data.appData.signupParams?.isAutoInsured,
            flowType: data.appData.flowType,
          },
        });
      };

      /**
       * If the `stepSection` has changed in the new app data provided, we switch
       * the active section.
       */
      if (newAppData && newAppData.stepSection !== data.appData.stepSection) {
        // NOTE: We can navigate to the next section using the following code.
        // However, if we do this, the user can press the browser's back button and enter
        // the previous section, which we don't want to happen.
        // Therefore, we simply reload the page to achieve:
        //    1) Automatic routing to the correct step in the new section.
        //    2) Prevent the user from pressing the back button and entering the previous section.
        setData({
          ...data,
          changingStepSection: true,
        });

        // Fire conversion events when a section is done.
        try {
          if (newAppData.stepSection === AppStepSection.Onboarding) {
            await trackSignedUp(newAppData);
          } else if (newAppData.stepSection === AppStepSection.Finished) {
            if (data.appData.stepSection === AppStepSection.Onboarding) {
              // To trigger AB test event
              getWebappNewUserOnboardingVariant();
            }
            if (newAppData.id) {
              // Garage only flow has no onboarding step. but we still need the signup events.
              if (isGarageFlow(newAppData)) {
                await trackSignedUp(newAppData);
              }
              await trackOnboarded(newAppData);
            }
          }
        } catch (err) {
          console.warn(err);
        }
        trackCompletedStepEvent();
        router.reload(); // NOTE: This will reload, and trackOnboarded's request could be cancelled.
        return true;
      }

      /**
       * Otherwise we are navigating within a step section.
       */
      const nextStep = getNextStep(newAppData);
      if (nextStep && canGoNext(newAppData)) {
        // Push current step on stack if it doesn't already exist.
        const shouldPushStep = !find(data.stepStack, (step) => step.id === stepID);
        if (shouldPushStep) {
          setData({
            ...data,
            direction: Direction.Right,
            appData: newAppData || data.appData,
            stepStack: [...data.stepStack, { id: stepID }],
          });
        } else if (newAppData) {
          setData({
            ...data,
            direction: Direction.Right,
            appData: newAppData,
          });
        } else {
          setData({
            ...data,
            direction: Direction.Right,
          });
        }

        // Navigate to next step.
        void trackEvent('OnboardingWillEnterStep', {
          eventMetadata: { path: nextStep.pathname },
        });
        trackCompletedStepEvent();

        const routerAction = action === 'replace' ? router.replace : router.push;

        return routerAction({
          pathname: nextStep.pathname,
          query: nextStep.queryParams?.(newAppData, router),
        });
      }

      // Track event if we failed to enter next step.
      void trackEvent('OnboardingEnterStepFailed', {
        eventMetadata: {
          path: router.pathname,
          nextPath: nextStep?.pathname || '',
          featureData: (newAppData || data.appData)?.featureData,
        },
      });

      return false;
    },
    [data, getNextStep, canGoNext, router, setData, getWebappNewUserOnboardingVariant, stepID]
  );

  const isLastOnboardingStep = useMemo(() => {
    const section = data.appData?.stepSection;
    const nextStep = getNextStep(data.appData);

    if (section === AppStepSection.Onboarding) {
      return !nextStep;
    }

    return false;
  }, [data.appData, getNextStep]);

  const isLastChallengeStep = useCallback(
    (nextAppData: AppData) => {
      const section = nextAppData?.stepSection;
      const nextStep = getNextStep(nextAppData);

      if (section === AppStepSection.Challenge) {
        return !nextStep;
      }

      return false;
    },
    [getNextStep]
  );

  /**
   * Go back.
   * Provide `numPages` to go back multiple pages.
   */
  const goBack = useCallback(
    (numPages = 1, skipChecking = false, direction = Direction.Left) => {
      setData((prev) => ({ ...prev, direction }));

      // We need to compute how many 'actual pages' to go back.
      // Note this can differ from the `numPages` value because we need to skip the steps
      // that don't allow 'back to self'.
      let back = numPages;

      if (!skipChecking) {
        const idx = getCurrentStepIdx();
        const prevSteps = getCanShowSteps().showSteps.slice(0, idx);
        // These are all the previous steps that allow 'back to self'.
        const allowedPrevSteps = filter(prevSteps, ({ allowBackToSelf = true }) => allowBackToSelf);
        // Make sure we have enough of these to go back given the `numPages` requested.
        if (allowedPrevSteps.length >= numPages) {
          // Calculate total number of pages we need to go back by finding the index of the allowable
          // previous step in the `prevSteps` array.
          const prevStepID = allowedPrevSteps[allowedPrevSteps.length - numPages].id;
          back = prevSteps.length - findLastIndex(prevSteps, (step) => step.id === prevStepID);
        }
      }

      // Next router doesn't support going back multiple pages, so we use the
      // history api directly if available.
      // If not available, we go back a single page.
      if (back > 1 && (skipChecking || canGoBack(back)) && window.history && window.history.go) {
        window.history.go(-back);
        return;
      }

      router.back();
    },
    [setData, canGoBack, router, getCurrentStepIdx, getCanShowSteps]
  );

  return {
    appData: data.appData,
    setAppData,
    stepIdx: getCurrentStepIdx(),
    stepCnt: getCanShowSteps().showSteps.length,
    goNext,
    canGoNext: canGoNext(),
    goBack,
    canGoBack: canGoBack(),
    direction: data.direction,
    stepIndicatorData: getStepIndicatorData(),
    isLastOnboardingStep,
    isLastChallengeStep,
  };
};
